import { isQueueViewKey, type QueueViewKey } from '../../../types/encounterWork';

/**
 * Home's selected queue in the URL (GDN-4085), so a biller who comes back
 * from the workbench lands on the tile they left.
 *
 * The rules are the Claims worklist's (`claims/claimWorkUrlState.ts`), and so
 * is the name: `view` is the key that tab already uses for which queue is
 * showing, with the same five values, so `/jotbill?view=review` on Home and
 * `/jotbill?tab=claims&view=review` name the same queue.
 * - It reads and writes ONLY its own key; `tab` and any foreign params pass
 *   through untouched (BillingV2 owns `tab`, and clears everything on a tab
 *   switch).
 * - No choice is absence: bare `/jotbill` stays clean and shows the default,
 *   the highest-priority queue with work.
 * - Parsing never throws: a value that is not one of the five queues falls
 *   back to the default.
 */

/** Every param key this module owns — cleared before writing, never more. */
export const HOME_PARAM_KEYS = ['view'] as const;

export interface HomeUrlState {
  /** The queue the biller chose, or null when they have not chosen one. */
  view: QueueViewKey | null;
}

export const parseHomeUrlState = (params: URLSearchParams): HomeUrlState => {
  const view = params.get('view');
  return { view: view !== null && isQueueViewKey(view) ? view : null };
};

/** State → params, into a COPY of the given params. Only this module's key is touched. */
export const applyHomeUrlState = (
  params: URLSearchParams,
  state: HomeUrlState,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  for (const key of HOME_PARAM_KEYS) next.delete(key);
  if (state.view) next.set('view', state.view);
  return next;
};

/**
 * Where the workbench's Back returns to: Home, on the queue the claim was
 * opened from. Named even when that queue was only the default, so a biller
 * who has just cleared it comes back to it reading "All clear" rather than to
 * whichever queue comes first now.
 */
export const homeReturnUrl = (params: URLSearchParams, view: QueueViewKey): string =>
  `/jotbill?${applyHomeUrlState(params, { view }).toString()}`;
